"use client";

/**
 * AlertProvider — app-wide toast service. Holds the queue of active alerts and
 * exposes `useAlert()` with typed helpers (`showSuccess` / `showError` / …) plus
 * `showApiError` for raw backend error envelopes. Rendering is delegated to the
 * presentational `CToast` (Figma 1169:149380); this provider only owns state +
 * auto-dismiss timing.
 */
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { CToast, type CToastType } from "@/Shared/CToast/CToast";
import formatApiError, {
  type ApiErrorPayload,
  type FormatApiErrorOptions,
} from "@/utils/formatApiError";

/** One queued toast. `type` mirrors `CToastType`. */
interface Alert {
  id: string;
  type: CToastType;
  /** Bold headline. */
  message: string;
  /** Optional sub-text shown under the headline. */
  description?: string;
  duration?: number;
}

interface AlertContextType {
  alerts: Alert[];
  showAlert: (
    type: CToastType,
    message: string,
    description?: string,
    duration?: number,
  ) => void;
  showSuccess: (message: string, description?: string) => void;
  showError: (message: string, description?: string) => void;
  showWarning: (message: string, description?: string) => void;
  showInfo: (message: string, description?: string) => void;
  /** Render a toast straight from a backend `{ error, message, details }`. */
  showApiError: (
    payload: ApiErrorPayload,
    options?: FormatApiErrorOptions & { duration?: number },
  ) => void;
  removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType>({
  alerts: [],
  showAlert: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  showApiError: () => {},
  removeAlert: () => {},
});

/** Access the toast service from any client component. */
export const useAlert = () => useContext(AlertContext);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Queue a toast and schedule its auto-dismiss (duration > 0).
  const showAlert = useCallback(
    (
      type: CToastType,
      message: string,
      description?: string,
      duration = 5000,
    ) => {
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setAlerts((prev) => [
        ...prev,
        { id, type, message, description, duration },
      ]);
      if (duration > 0) {
        setTimeout(() => removeAlert(id), duration);
      }
    },
    [removeAlert],
  );

  const showSuccess = useCallback(
    (message: string, description?: string) =>
      showAlert("success", message, description),
    [showAlert],
  );
  const showError = useCallback(
    (message: string, description?: string) =>
      showAlert("error", message, description),
    [showAlert],
  );
  const showWarning = useCallback(
    (message: string, description?: string) =>
      showAlert("warning", message, description),
    [showAlert],
  );
  const showInfo = useCallback(
    (message: string, description?: string) =>
      showAlert("info", message, description),
    [showAlert],
  );

  // Normalise a backend error envelope into title + sub-text, then show it.
  // `resolveId` turns any id the backend echoed (e.g. a space UUID) into a name.
  const showApiError = useCallback(
    (
      payload: ApiErrorPayload,
      options?: FormatApiErrorOptions & { duration?: number },
    ) => {
      const { title, description } = formatApiError(payload, {
        resolveId: options?.resolveId,
      });
      showAlert("error", title, description, options?.duration);
    },
    [showAlert],
  );

  const value = useMemo(
    () => ({
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showApiError,
      removeAlert,
    }),
    [
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showApiError,
      removeAlert,
    ],
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      {/* Toast stack — fixed top-right, newest at the bottom, click-through gaps.
          z-[100] sits above drawers/dialogs (Radix overlays use z-50 and portal
          to the end of <body>, so an equal z-index would hide the toast). */}
      <div className="pointer-events-none fixed top-4 right-4 z-[100] flex flex-col items-end gap-3">
        {alerts.map((alert) => (
          <div key={alert.id} className="pointer-events-auto">
            <CToast
              type={alert.type}
              title={alert.message}
              description={alert.description}
              onClose={() => removeAlert(alert.id)}
            />
          </div>
        ))}
      </div>
    </AlertContext.Provider>
  );
}
